import React, { useEffect } from 'react'
import { RouteComponentProps, RouteProps, useLocation } from 'react-router'
import { Redirect, Route, Switch } from 'react-router-dom'

import { log } from '../../logger'
import { useAppState } from '../../state'
import { Links, Paths } from './paths'

import { TenantsPage } from '../../pages/tenants/tenants-page'
import { ChargerPage } from '../../pages/chargers/charger-page'
import { Auth0LoginPage } from '../../pages/authentication/auth0-login-page'
import { Auth0CallbackFunc } from '../../pages/authentication/auth0-callback-page'
import { CognitoLoginPage } from '../../pages/authentication/cognito-login-page'
import { CognitoCallbackPage } from '../../pages/authentication/cognito-callback-page'
import { CognitoLogoutPage } from '../../pages/authentication/cognito-logout-page'
import { TenantSelectPage } from '../../pages/authentication/tenant-selector-page'

import { DashboardPage } from '../../pages/dashboard/dashboard-home-page'
import { V3DashboardPage } from '../../pages/dashboard/v3-dashboard-home-page'
// import { ListingCreatePageV2 } from '../../pages/listing/listing-create-page-v2'
import { ListingPageV2 } from '../../pages/listing/listing-page-v2'
import { TenantCreatePage } from '../../pages/tenants/tenant-create-page'
import { TenantPage } from '../../pages/tenants/tenant-page'
import { ReportsPage } from '../../pages/reports/reports-page'
import { PeakShavingProgramPage } from '../../pages/peakShaving/peak-shaving-program-page'
import { PeakShavingProgramsPage } from '../../pages/peakShaving/peak-shaving-programs-page'
import { ClassicUsersPage } from '../../pages/users/classic-users-page'
import { UsersHomePage } from '../../pages/users/users-home-page'
import configProvider from 'config'
import { TaggingPage } from 'pages/tagging/tagging-page'
import { ChargersPage } from 'pages/chargers/chargers-page'
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics'
import { DemandManagementPage } from 'pages/demandManagement/demand-management-page'
import { TransactionsPage } from 'pages/transactions/TransactionsPage'
import { V3TransactionsPage } from 'pages/v3-transactions/V3TransactionsPage'
import { UserWalletPage } from 'pages/users/wallet/user-wallet-page'
import { IntegrationsMarketplacePage } from 'pages/integrations-marketplace/integrations-marketplace-page'
import { TaxPage } from 'pages/tax/tax-page'
import {
  isChargerViewerToTenants,
  isDashboardViewerToTenants,
  isManagerToTenants,
  isViewerToTenants,
} from 'models/user'

export const LoggedInPath = Links.onboard()

export const Routes: React.FC = () => {
  const location = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  const authenticatedRoutes: {
    [path: string]: {
      component: React.ComponentType<any>
      accessibility: {
        admin: boolean
        manager: boolean
        user: boolean
        chargerViewer?: boolean
        dashboardViewer?: boolean
      }
    }
  } = {
    [Paths.onboard]: { component: TenantSelectPage, accessibility: { user: true, admin: true, manager: true } },
    [Paths.dashboard]: {
      component: V3DashboardPage,
      accessibility: { user: true, admin: true, manager: true, dashboardViewer: true },
    },
    [Paths.v1Dashboard]: {
      component: DashboardPage,
      accessibility: { user: true, admin: true, manager: true, dashboardViewer: true },
    },
    [Paths.chargers]: {
      component: ChargersPage,
      accessibility: { chargerViewer: true, user: true, admin: true, manager: true },
    },
    [Paths.charger]: {
      component: ChargerPage,
      accessibility: { chargerViewer: true, user: true, admin: true, manager: true },
    },
    // [Paths.listingCreate]: {
    //   component: ListingCreatePageV2,
    //   accessibility: { user: false, admin: true, manager: false },
    // },
    [Paths.listing]: { component: ListingPageV2, accessibility: { user: false, admin: true, manager: false } },
    [Paths.transactions]: {
      component: V3TransactionsPage,
      accessibility: { user: true, admin: true, manager: true },
    },
    [Paths.v1Transactions]: {
      component: TransactionsPage,
      accessibility: { user: true, admin: true, manager: true },
    },
    [Paths.tenants]: { component: TenantsPage, accessibility: { user: false, admin: true, manager: true } },
    [Paths.tenant]: { component: TenantPage, accessibility: { user: false, admin: true, manager: true } },
    [Paths.tenantsCreate]: { component: TenantCreatePage, accessibility: { user: false, admin: true, manager: false } },
    [Paths.reports]: { component: ReportsPage, accessibility: { user: true, admin: true, manager: true } },
    [Paths.users]: { component: UsersHomePage, accessibility: { user: true, admin: true, manager: true } },
    [Paths.peakShavingPrograms]: {
      component: configProvider.config.network === 'powercharge' ? DashboardPage : PeakShavingProgramsPage,
      accessibility: { user: false, admin: true, manager: false },
    },
    [Paths.peakShavingProgram]: {
      component: configProvider.config.network === 'powercharge' ? DashboardPage : PeakShavingProgramPage,
      accessibility: { user: false, admin: true, manager: false },
    },
    [Paths.demandManagement]: {
      component: configProvider.config.network === 'powercharge' ? DashboardPage : DemandManagementPage,
      accessibility: { user: false, admin: configProvider.config.network === 'swtch' || configProvider.config.network === 'EPB', manager: false },
    },
    [Paths.classicUsersPage]: {
      component: ClassicUsersPage,
      accessibility: { user: true, admin: true, manager: true },
    },
    ...(configProvider.config.network !== 'demo' && {
      [Paths.tagging]: {
        component: TaggingPage,
        accessibility: { user: false, admin: true, manager: false },
      },
    }),
    [Paths.integrationsMarketplace]: {
      component: IntegrationsMarketplacePage,
      accessibility: { user: true, admin: true, manager: true, dashboardViewer: true },
    },
    [Paths.userWallet]: {
      component: UserWalletPage,
      accessibility: { user: false, admin: true, manager: false },
    },
    [Paths.tax]: {
      component: TaxPage,
      accessibility: { user: false, admin: true, manager: false },
    },
  }

  useGoogleAnalytics(authenticatedRoutes)

  return (
    <>
      <Switch>
        {/* Authenticated-only routes */}

        {/* redirect users from listings to chargers */}
        <Route exact path={Paths.listings}>
          <Redirect to={Paths.chargers} />
        </Route>
        <Route exact path={Paths.v3Dashboard}>
          <Redirect to={Paths.dashboard} />
        </Route>
        <Route exact path={Paths.v3Transactions}>
          <Redirect to={Paths.transactions} />
        </Route>
        <Route exact path="/v1_transactions">
          <Redirect to={Paths.v1Transactions} />
        </Route>
        <Route exact path="/v3_transaction">
          <Redirect to={Paths.transactions} />
        </Route>
        {Object.keys(authenticatedRoutes).map((path) => {
          return (
            <AuthenticatedRoute
              exact={true}
              key={`authenticated-route${path.replace('/', '-')}`}
              path={path}
              component={authenticatedRoutes[path].component}
              accessibility={authenticatedRoutes[path].accessibility}
            />
          )
        })}
        {/* Unauthenticated-only routes */}
        <UnauthenticatedRoute exact={true} path={Paths.auth0login} component={Auth0LoginPage} />
        <UnauthenticatedRoute exact={true} path={Paths.cognitoLogin} component={CognitoLoginPage} />
        <Route path={Paths.auth0Callback} exact={true} component={Auth0CallbackFunc} />
        <Route path={Paths.cognitoCallback} exact={true} component={CognitoCallbackPage} />
        <Route path={Paths.cognitoLogout} exact={true} component={CognitoLogoutPage} />
        <Redirect to={configProvider.config.authProvider === 'cognito' ? Paths.cognitoLogin : Paths.auth0login} />
      </Switch>
    </>
  )
}

/**
 * Returns a ReactRouter.Route that can only be accessed with a valid
 * session. Accessing it with an invalid or null session with redirect
 * the user to Auth0. Upon authentication, the user will be redirect
 * to it's original route.
 */
function AuthenticatedRoute(
  props: RouteProps & {
    accessibility: {
      admin: boolean
      manager: boolean
      user: boolean
      chargerViewer?: boolean
      dashboardViewer?: boolean
    }
  },
) {
  const { isAuthenticated, currentUser } = useAppState()
  const Component = props.component as React.ComponentType<any>
  const rest = Object.assign({}, props)
  const accessibility = props.accessibility
  delete rest.component

  // admin > manager > viewer > charger_viewer > dashboard_viewer
  const isAdmin = currentUser?.role === 'admin'
  const isManager = isAdmin || (currentUser?.role === 'user' && isManagerToTenants(currentUser?.accesses))
  const isViewer = isManager || (currentUser?.role === 'user' && isViewerToTenants(currentUser?.accesses))
  const isChargerViewer = isViewer || (currentUser?.role === 'user' && isChargerViewerToTenants(currentUser?.accesses))
  const isDashboardViewer =
    isViewer || (currentUser?.role === 'user' && isDashboardViewerToTenants(currentUser?.accesses))
  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps): React.ReactNode => {
        // If the user is not authenticated, redirect to signup
        if (!isAuthenticated()) {
          return <Redirect to={configProvider.config.authProvider === 'auth0' ? Links.auth0login() : Links.cognitoLogin()} />
        }

        // is the user is authenticated and he wants to go
        // to the onboarding flow send him there
        if (props.location.pathname === '/onboard') {
          return <Component {...props} />
        }

        // is the current user has not been resolved (i.e. GetSelf was NOT called on the onboarding flow)
        // send him back to onboarding to attempt to call GetSelf
        if (!currentUser) {
          log(
            'attempted to load an authentication page, current user is not set redirecting to onboard to resolve current user',
            { desiredPath: props.location.pathname },
          )
          return <Redirect to={`${Links.onboard()}?redirect_path=${props.location.pathname}${props.location.search}`} />
        }

        let redirectToOnboard = true

        if (currentUser.role === 'user') {
          if (accessibility.chargerViewer && isChargerViewer && redirectToOnboard) {
            redirectToOnboard = false
          }
          if (accessibility.user && isViewer && redirectToOnboard) {
            redirectToOnboard = false
          }
          if (accessibility.manager && isManager && redirectToOnboard) {
            redirectToOnboard = false
          }
          if (accessibility.dashboardViewer && isDashboardViewer && redirectToOnboard) {
            redirectToOnboard = false
          }
        }
        if (accessibility.admin && isAdmin && redirectToOnboard) {
          redirectToOnboard = false
        }

        if (redirectToOnboard) {
          return <Redirect to={Links.onboard()} />
        }

        // SWTCH community only: block direct URL access when not SWTCH
        const pathname = props.location.pathname
        if (pathname === Paths.integrationsMarketplace) {
          if (configProvider.config.communityIdent !== 'SWTCH' || configProvider.config.siteTitle !== 'SWTCH') {
            return <Redirect to={Links.dashboard()} />
          }
        }
        if (pathname === Paths.tax) {
          if (configProvider.config.communityIdent !== 'SWTCH' || configProvider.config.siteTitle !== 'SWTCH') {
            return <Redirect to={Links.dashboard()} />
          }
        }

        return <Component {...props} />
      }}
    />
  )
}

/**
 * Returns a ReactRouter.Route that can only be accessed with a valid
 * session. Accessing it with an invalid or null session with redirect
 * the user to Auth0. Upon authentication, the user will be redirect
 * to it's original route.
 */
function UnauthenticatedRoute(props: RouteProps) {
  const { isAuthenticated } = useAppState()
  const Component = props.component as React.ComponentType<any>
  const rest = Object.assign({}, props)
  delete rest.component
  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps) => {
        if (isAuthenticated()) {
          // If the user is authenticated and this is an auth page, redirect to onboarding
          return <Redirect to={Links.onboard()} />
        }
        return <Component {...props} />
      }}
    />
  )
}
